import { ApiError, NetworkError } from './errors';
import type { ApiResponse } from './types';

export interface ApiClientOptions {
  baseUrl: string;
  /** Called when the backend returns 401/40101 — typically redirect to /login. */
  onUnauthorized?: () => void;
  /** Called when the backend returns 429 — typically a toast like "请求过于频繁". */
  onRateLimited?: (retryAfterSeconds: number | null) => void;
  /** Optional global error reporter (forwarded to the toast layer by default). */
  onError?: (error: ApiError | NetworkError) => void;
  /** Extra headers applied to every request (e.g. tenant Host overrides in dev). */
  defaultHeaders?: Record<string, string>;
  /** Called once per request before fetch — last chance to mutate headers (token, etc). */
  beforeRequest?: (request: Request) => Request | Promise<Request>;
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  headers?: Record<string, string>;
  /** When true, treat non-zero `code` as success and return raw payload. */
  rawResponse?: boolean;
}

export class ApiClient {
  private readonly options: ApiClientOptions;

  constructor(options: ApiClientOptions) {
    this.options = options;
  }

  get<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, opts);
  }

  post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, { ...opts, body });
  }

  put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, { ...opts, body });
  }

  patch<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, { ...opts, body });
  }

  delete<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, opts);
  }

  /**
   * Fetch a binary payload (e.g. an .xlsx export) as a Blob. Unlike {@link request}, this never
   * tries to JSON-decode the body, so binary content is preserved. On a non-2xx response it still
   * routes 401/429 through the shared listeners and throws an {@link ApiError}, attempting to read a
   * JSON error envelope for the message when present.
   */
  async getBlob(path: string, opts: RequestOptions = {}): Promise<Blob> {
    const url = this.buildUrl(path, opts.query);
    const headers: Record<string, string> = {
      ...(this.options.defaultHeaders ?? {}),
      ...(opts.headers ?? {}),
    };
    const { body: _ignoredBody, headers: _ignoredHeaders, query: _ignoredQuery, ...passThrough } =
      opts;
    void _ignoredBody;
    void _ignoredHeaders;
    void _ignoredQuery;
    let request = new Request(url, {
      ...passThrough,
      method: 'GET',
      headers,
      credentials: 'include',
    });
    if (this.options.beforeRequest) {
      request = await this.options.beforeRequest(request);
    }

    let response: Response;
    try {
      response = await fetch(request);
    } catch (cause) {
      const networkError = new NetworkError('网络请求失败，请检查连接', cause);
      this.options.onError?.(networkError);
      throw networkError;
    }

    if (response.status === 429) {
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      this.options.onRateLimited?.(retryAfter);
    }

    if (!response.ok) {
      let message = response.statusText;
      try {
        const json = (await response.clone().json()) as { message?: unknown };
        if (json && typeof json.message === 'string') {
          message = json.message;
        }
      } catch {
        // Body was not a JSON error envelope; keep the status text.
      }
      const error = new ApiError(response.status, response.status, message);
      if (error.isUnauthorized()) {
        this.options.onUnauthorized?.();
      }
      this.options.onError?.(error);
      throw error;
    }

    return response.blob();
  }

  private async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const url = this.buildUrl(path, opts.query);
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(this.options.defaultHeaders ?? {}),
      ...(opts.headers ?? {}),
    };

    let payload: BodyInit | undefined;
    if (opts.body !== undefined && opts.body !== null) {
      if (opts.body instanceof FormData) {
        payload = opts.body;
      } else {
        headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
        payload = JSON.stringify(opts.body);
      }
    }

    const { body: _ignoredBody, headers: _ignoredHeaders, query: _ignoredQuery, ...passThrough } =
      opts;
    void _ignoredBody;
    void _ignoredHeaders;
    void _ignoredQuery;
    let request = new Request(url, {
      ...passThrough,
      method,
      headers,
      body: payload,
      credentials: 'include',
    });
    if (this.options.beforeRequest) {
      request = await this.options.beforeRequest(request);
    }

    let response: Response;
    try {
      response = await fetch(request);
    } catch (cause) {
      const networkError = new NetworkError('网络请求失败，请检查连接', cause);
      this.options.onError?.(networkError);
      throw networkError;
    }

    if (response.status === 429) {
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      this.options.onRateLimited?.(retryAfter);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get('Content-Type') ?? '';
    if (!contentType.includes('application/json')) {
      if (response.ok) {
        return (await response.text()) as unknown as T;
      }
      const error = new ApiError(response.status, response.status, await response.text());
      this.options.onError?.(error);
      throw error;
    }

    const json = (await response.json()) as ApiResponse<T> | T;

    if (opts.rawResponse) {
      if (!response.ok) {
        const error = new ApiError(
          response.status,
          response.status,
          'Request failed: ' + response.status,
        );
        this.options.onError?.(error);
        throw error;
      }
      return json as T;
    }

    if (!isApiResponse(json)) {
      if (!response.ok) {
        const error = new ApiError(response.status, response.status, response.statusText);
        this.options.onError?.(error);
        throw error;
      }
      return json as T;
    }

    if (json.code !== 0) {
      const error = new ApiError(response.status, json.code, json.message);
      if (error.isUnauthorized()) {
        this.options.onUnauthorized?.();
      }
      this.options.onError?.(error);
      throw error;
    }

    return json.data as T;
  }

  private buildUrl(path: string, query?: RequestOptions['query']): string {
    const url = path.startsWith('http')
      ? new URL(path)
      : new URL(this.joinBaseAndPath(path), window.location.origin);
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value === null || value === undefined) continue;
        url.searchParams.append(key, String(value));
      }
    }
    if (path.startsWith('http')) {
      return url.toString();
    }
    return url.pathname + url.search;
  }

  private joinBaseAndPath(path: string): string {
    const base = this.options.baseUrl.replace(/\/$/, '');
    const tail = path.startsWith('/') ? path : `/${path}`;
    return base + tail;
  }
}

function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const seconds = Number.parseInt(header, 10);
  if (Number.isFinite(seconds)) return seconds;
  const date = Date.parse(header);
  if (Number.isNaN(date)) return null;
  return Math.max(0, Math.round((date - Date.now()) / 1000));
}

function isApiResponse(value: unknown): value is ApiResponse<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'message' in value &&
    'data' in value &&
    typeof (value as ApiResponse<unknown>).code === 'number'
  );
}
